import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, Users, Baby, CalendarDays, Shirt, Check, MapPin, BellRing } from 'lucide-react';
import { EmailService } from '../utils/emailService';
import { AnalyticsService } from '../utils/analyticsService';
import type { AgeGroup, CampRegistration, TshirtSize, TshirtDesign } from '../types';
import { SEASON } from '../../shared/season.js';

const tshirtSizes: { value: TshirtSize; label: string }[] = [
  { value: '100', label: '100 cm (bērnu)' },
  { value: '110', label: '110 cm (bērnu)' },
  { value: '120', label: '120 cm (bērnu)' },
  { value: '130', label: '130 cm (bērnu)' },
  { value: '140', label: '140 cm (bērnu)' },
  { value: '150', label: '150 cm (bērnu)' },
  { value: 'XS', label: 'XS' },
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'XL', label: 'XL' },
  { value: 'XXL', label: 'XXL' },
];

// TODO before opening 2027 registration: these are the 2026 t-shirt
// designs — refresh the images/names (public/whatsapp*.jpeg) or drop the
// picker if the 2027 designs aren't ready yet.
const tshirtDesigns: { value: TshirtDesign; image: string; alt: string }[] = [
  { value: 'Saule', image: '/whatsapp1.jpeg', alt: 'DZĪVS MŪŽĪGI — saules dizains' },
  { value: 'Lāpa', image: '/whatsapp2.jpeg', alt: 'DZĪVS MŪŽĪGI — lāpas dizains' },
  { value: 'Pulkstenis', image: '/whatsapp3.jpeg', alt: 'DZĪVS MŪŽĪGI — pulksteņa dizains' },
];

const ageGroupOptions: {
  value: AgeGroup;
  label: string;
  dates: string;
  Icon: typeof Users;
  ringColor: string;
  iconBg: string;
}[] = [
  {
    value: 'teens',
    label: SEASON.groups.teens.label,
    dates: SEASON.groups.teens.datesWithYear,
    Icon: Users,
    ringColor: 'ring-accent-500',
    iconBg: 'from-accent-500 to-accent-600',
  },
  {
    value: 'kids',
    label: SEASON.groups.kids.label,
    dates: SEASON.groups.kids.datesWithYear,
    Icon: Baby,
    ringColor: 'ring-primary-500',
    iconBg: 'from-primary-500 to-primary-600',
  },
];

// Shown while SEASON.registrationOpen is false: the season's key facts and
// a "sign-up opens later" note instead of the form.
const RegistrationClosed: React.FC = () => {
  useEffect(() => {
    // Drop any queued submissions from a past season — they must never
    // replay into the new season's collections once registration reopens.
    try {
      localStorage.removeItem('sw_pending_registrations');
    } catch {
      // localStorage unavailable — nothing to clean
    }
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
            Reģistrācija
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Pieteikšanās {SEASON.year}. gada nometnei vēl nav atvērta.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 border border-gray-100"
          >
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-gray-700 font-medium mb-8">
              <span className="inline-flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary-600" />
                {SEASON.weekRange}
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary-600" />
                Stadions Viļānos
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ageGroupOptions.map(({ value, label, dates, Icon, iconBg }) => (
                <div key={value} className="rounded-2xl border-2 border-gray-200 bg-white p-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${iconBg} flex items-center justify-center shadow`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{label}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {dates}
                      </p>
                      <p className="text-xs text-gray-400">
                        {SEASON.groups[value === 'teens' ? 'teens' : 'kids'].weekdaysShort}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl bg-primary-50 border border-primary-200 p-5 flex items-start gap-3">
              <BellRing className="w-6 h-6 text-primary-600 shrink-0 mt-0.5" />
              <p className="text-gray-700">
                <span className="font-semibold">Pieteikšanās tiks atvērta tuvāk nometnei.</span>{' '}
                Dalība ir bez maksas. Seko līdzi šeit un mūsu sociālajos tīklos, lai nepalaistu garām!
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const RegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState<Omit<CampRegistration, 'id' | 'registrationDate'>>({
    name: '',
    age: 0,
    ageGroup: null,
    email: '',
    phone: '',
    parentName: '',
    parentEmail: '',
    tshirtSize: '',
    tshirtDesign: '',
    additionalInfo: '',
    agreement: false,
  });
  const [kidsCount, setKidsCount] = useState(0);
  const [teensCount, setTeensCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'duplicate'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasStartedForm, setHasStartedForm] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);

  // The form is long and the result banners render at its top — without an
  // explicit scroll, a person at the submit button never sees the outcome.
  const scrollToStatus = () => {
    window.setTimeout(() => {
      statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const maxKids = SEASON.groups.kids.capacity;
  const maxTeens = SEASON.groups.teens.capacity;
  // Real registration counts only — no display baseline, so "full" means
  // actually full and no capacity is silently lost.
  const displayedKids = kidsCount;
  const displayedTeens = teensCount;
  const selectedGroupFull =
    (formData.ageGroup === 'kids' && kidsCount >= maxKids) ||
    (formData.ageGroup === 'teens' && teensCount >= maxTeens);

  useEffect(() => {
    fetchRegistrationCount();
    try {
      const stored = sessionStorage.getItem('sw_ageGroup');
      if (stored === 'kids' || stored === 'teens') {
        setFormData((prev) => ({ ...prev, ageGroup: stored }));
        sessionStorage.removeItem('sw_ageGroup');
      }
    } catch {
      // sessionStorage may be unavailable; ignore
    }
    // Replay any registrations that were queued locally because a prior
    // submit failed to reach Firestore. Each queued item is re-sent via the
    // admin-notification email, then the local copy is removed.
    void replayPendingRegistrations();
  }, []);

  const replayPendingRegistrations = async () => {
    try {
      const raw = localStorage.getItem('sw_pending_registrations');
      if (!raw) return;
      const queue: Array<Omit<CampRegistration, 'id'> & { _queuedAt?: string }> = JSON.parse(raw);
      if (!Array.isArray(queue) || queue.length === 0) return;
      const remaining: typeof queue = [];
      for (const item of queue) {
        try {
          const result = await EmailService.sendWelcomeEmail(item as any);
          if (!result.success) remaining.push(item);
        } catch {
          remaining.push(item);
        }
      }
      if (remaining.length === 0) localStorage.removeItem('sw_pending_registrations');
      else localStorage.setItem('sw_pending_registrations', JSON.stringify(remaining));
    } catch (err) {
      console.warn('[Registration] Replay of queued registrations failed:', err);
    }
  };

  // Counts come from the server. The client never reads Firestore docs.
  const fetchRegistrationCount = async () => {
    try {
      const res = await fetch('/api/counts', { method: 'GET' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { kids?: number; teens?: number };
      setKidsCount(typeof data.kids === 'number' ? data.kids : 0);
      setTeensCount(typeof data.teens === 'number' ? data.teens : 0);
    } catch (err) {
      console.warn('[Registration] Could not load counts:', err);
      setKidsCount(0);
      setTeensCount(0);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.ageGroup) {
      newErrors.ageGroup = 'Lūdzu izvēlies nometni';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Vārds ir obligāts';
    }
    if (!formData.age || formData.age <= 0) {
      newErrors.age = 'Lūdzu ievadiet derīgu vecumu';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-pasts ir obligāts';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Nepareizs e-pasta formāts';
    }
    if (formData.age > 0 && formData.age < 18) {
      if (!formData.parentName?.trim()) {
        newErrors.parentName = 'Vecāka/aizbildņa vārds ir obligāts';
      }
      if (!formData.phone?.trim()) {
        newErrors.phone = 'Telefons ir obligāts';
      }
    }
    if (!formData.tshirtSize) {
      newErrors.tshirtSize = 'Lūdzu izvēlies T-krekla izmēru';
    }
    if (!formData.agreement) {
      newErrors.agreement = 'Jāpiekrīt noteikumiem un nosacījumiem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      AnalyticsService.trackFormInteraction('registration', 'error');
      return;
    }

    AnalyticsService.trackFormInteraction('registration', 'start');
    setIsSubmitting(true);

    // Single server call does dedup → Firestore write → emails.
    // The duplicate check is authoritative; the client never touches PII.
    const result = await EmailService.sendWelcomeEmail(formData);

    setIsSubmitting(false);

    if (result.duplicate) {
      setSubmitStatus('duplicate');
      scrollToStatus();
      AnalyticsService.trackFormInteraction('registration', 'error');
      return;
    }

    if (!result.success) {
      // Last-resort: queue locally so we can replay later, then show error.
      try {
        const queueRaw = localStorage.getItem('sw_pending_registrations');
        const queue = queueRaw ? JSON.parse(queueRaw) : [];
        queue.push({
          ...formData,
          email: formData.email.trim().toLowerCase(),
          registrationDate: new Date().toISOString(),
          _queuedAt: new Date().toISOString(),
        });
        localStorage.setItem('sw_pending_registrations', JSON.stringify(queue));
      } catch (queueErr) {
        console.error('[Registration] Could not queue to localStorage:', queueErr);
      }
      setSubmitStatus('error');
      scrollToStatus();
      return;
    }

    // Success path.
    setSubmitStatus('success');
    scrollToStatus();
    AnalyticsService.trackFormInteraction('registration', 'complete');
    AnalyticsService.trackRegistrationConversion();

    const ageGroupAtSubmit = formData.ageGroup;
    setFormData({
      name: '',
      age: 0,
      ageGroup: null,
      email: '',
      phone: '',
      parentName: '',
      parentEmail: '',
      tshirtSize: '',
      tshirtDesign: '',
      additionalInfo: '',
      agreement: false,
    });

    // Optimistic count bump for instant feedback, then reconcile with the server.
    if (ageGroupAtSubmit === 'kids') setKidsCount((n) => n + 1);
    if (ageGroupAtSubmit === 'teens') setTeensCount((n) => n + 1);
    fetchRegistrationCount();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (!hasStartedForm) {
      setHasStartedForm(true);
      AnalyticsService.trackEvent('registration_form_started', { first_field: name });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleGroupSelect = (group: AgeGroup) => {
    if (!hasStartedForm) {
      setHasStartedForm(true);
      AnalyticsService.trackEvent('registration_form_started', { first_field: 'ageGroup' });
    }
    setFormData((prev) => ({ ...prev, ageGroup: group }));
    if (errors.ageGroup) {
      setErrors((prev) => ({ ...prev, ageGroup: '' }));
    }
  };

  const handleDesignSelect = (design: TshirtDesign) => {
    // Clicking the already-selected design clears it (lets people opt out).
    setFormData((prev) => ({
      ...prev,
      tshirtDesign: prev.tshirtDesign === design ? '' : design,
    }));
  };

  const handleFormFocus = () => {
    if (!hasStartedForm) {
      setHasStartedForm(true);
      AnalyticsService.trackEvent('registration_form_focused');
    }
  };

  const ageGroupMismatch =
    formData.ageGroup && formData.age > 0
      ? (formData.ageGroup === 'kids' && formData.age > (SEASON.groups.kids.maxAge ?? Infinity)) ||
        (formData.ageGroup === 'teens' && formData.age < (SEASON.groups.teens.minAge ?? 0))
      : false;

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
            Reģistrācija
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Reģistrējies jau tagad — pilnu programmu un dienas kārtību nosūtīsim e-pastā tuvāk nometnes datumam.
          </p>
        </motion.div>

        {/* Registration Progress — per group */}
        <div className="max-w-4xl mx-auto mb-12">
          <h3 className="text-xl md:text-2xl font-bold mb-4 text-center">Reģistrācijas progress</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-accent-600" />
                  <p className="font-semibold text-gray-800">{SEASON.groups.teens.label}</p>
                </div>
                <div className="text-right">
                  <span className="text-base font-semibold">{Math.min(displayedTeens, maxTeens)} / {maxTeens}</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-gradient-to-r from-accent-500 to-accent-600 rounded-full h-2.5 transition-all"
                  style={{ width: `${Math.min((displayedTeens / maxTeens) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">{SEASON.groups.teens.datesWithYear}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Baby className="w-5 h-5 text-primary-600" />
                  <p className="font-semibold text-gray-800">{SEASON.groups.kids.label}</p>
                </div>
                <div className="text-right">
                  <span className="text-base font-semibold">{Math.min(displayedKids, maxKids)} / {maxKids}</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-full h-2.5 transition-all"
                  style={{ width: `${Math.min((displayedKids / maxKids) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">{SEASON.groups.kids.datesWithYear}</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 border border-gray-100"
          >
            <div ref={statusRef}>
            <AnimatePresence>
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-8"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-emerald-800 font-bold text-lg">Reģistrācija veiksmīga!</h3>
                      <p className="text-emerald-700">
                        Paldies par reģistrāciju! Tuvāk nometnes datumam nosūtīsim e-pastā pilnu programmu un dienas kārtību.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'duplicate' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-7 h-7 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-amber-800 font-bold text-lg">Šī reģistrācija jau ir saņemta</h3>
                      <p className="text-amber-700">
                        Mēs jau esam saņēmuši pieteikumu ar šādu vārdu, e-pastu un vecumu. Ja tas ir kļūda, lūdzu sazinies ar mums.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8"
                >
                  <div className="flex items-start gap-3">
                    <XCircle className="w-7 h-7 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-red-800 font-bold text-lg">Reģistrācija neizdevās</h3>
                      <p className="text-red-700">
                        Radās tehniska problēma. Lūdzu mēģiniet vēlāk vai sazinieties ar mums.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Age-group selector (first, required) */}
              <div>
                <label className="block text-gray-700 font-medium mb-3">
                  Izvēlies nometni *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ageGroupOptions.map(({ value, label, dates, Icon, ringColor, iconBg }) => {
                    const selected = formData.ageGroup === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleGroupSelect(value)}
                        className={`text-left rounded-2xl border-2 p-4 transition-all ${
                          selected
                            ? `border-transparent ring-2 ${ringColor} bg-white shadow-md`
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        aria-pressed={selected}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${iconBg} flex items-center justify-center shadow`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-bold text-gray-800">{label}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <CalendarDays className="w-3.5 h-3.5" />
                              {dates}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {errors.ageGroup && <p className="text-red-500 text-sm mt-2">{errors.ageGroup}</p>}
                {ageGroupMismatch && (
                  <p className="text-amber-600 text-sm mt-2">
                    Norādītais vecums neatbilst izvēlētajai nometnei.
                  </p>
                )}
              </div>

              {/* Name and Age */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Vārds un uzvārds *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onFocus={handleFormFocus}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ievadiet pilno vārdu"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Vecums *
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age || ''}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                      errors.age ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ievadiet vecumu"
                  />
                  {errors.age && <p className="text-red-500 text-sm mt-1">{errors.age}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">E-pasts *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="juris@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* T-shirt — design + size. Size is required; a t-shirt is no longer guaranteed. */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 md:p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow shrink-0">
                      <Shirt className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        T-krekls{' '}
                        <span className="align-middle inline-block text-[11px] font-semibold tracking-wide text-primary-700 bg-primary-100 rounded-full px-2 py-0.5">
                          DZĪVS MŪŽĪGI
                        </span>
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Izvēlies dizainu un izmēru. Diemžēl nevaram garantēt, ka T-krekls būs pieejams visiem, taču izmēru lūdzam norādīt.
                      </p>
                    </div>
                  </div>

                  {/* Design picker */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-3">Dizains</label>
                    <div className="grid grid-cols-3 gap-3">
                      {tshirtDesigns.map(({ value, image, alt }) => {
                        const selected = formData.tshirtDesign === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => handleDesignSelect(value)}
                            aria-pressed={selected}
                            className={`group relative rounded-2xl border-2 overflow-hidden bg-white transition-all ${
                              selected
                                ? 'border-transparent ring-2 ring-primary-500 shadow-md'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <img
                              src={image}
                              alt={alt}
                              loading="lazy"
                              className="aspect-square w-full object-cover"
                            />
                            {selected && (
                              <span className="absolute top-2 right-2 bg-primary-600 text-white rounded-full p-1 shadow">
                                <Check className="w-3.5 h-3.5" />
                              </span>
                            )}
                            <span className="block text-center text-xs sm:text-sm font-medium text-gray-700 py-2">
                              {value}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Size */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Izmērs *</label>
                    <select
                      name="tshirtSize"
                      value={formData.tshirtSize ?? ''}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                        errors.tshirtSize ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">— izvēlies izmēru —</option>
                      {tshirtSizes.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    {errors.tshirtSize && <p className="text-red-500 text-sm mt-1">{errors.tshirtSize}</p>}
                  </div>
                </div>

              {/* Parent/Guardian contact (shown when age < 18) */}
              <AnimatePresence initial={false}>
                {formData.age > 0 && formData.age < 18 && (
                  <motion.div
                    key="parent-block"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 bg-blue-50 rounded-xl border border-blue-200 space-y-4">
                      <h3 className="text-lg font-semibold text-blue-800">Vecāka/aizbildņa kontakts</h3>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">
                          Vecāka/aizbildņa vārds *
                        </label>
                        <input
                          type="text"
                          name="parentName"
                          value={formData.parentName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                            errors.parentName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Vecāka pilnais vārds"
                        />
                        {errors.parentName && <p className="text-red-500 text-sm mt-1">{errors.parentName}</p>}
                      </div>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">
                          Telefons *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                            errors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="+371 12345678"
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Combined notes — allergies, food preferences, anything else */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Alerģijas, ēšanas īpatnības vai cita informācija <span className="text-gray-500 font-normal">(neobligāti)</span>
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                  placeholder="Piem., laktozes nepanesība, vegetārietis, alerģija pret riekstiem, kā arī jebkas cits, ko mums būtu jāzina..."
                />
              </div>

              {/* Agreement */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="agreement"
                  checked={formData.agreement}
                  onChange={handleInputChange}
                  className="mt-1 h-5 w-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label className="text-gray-700">
                  <span className="font-medium">Piekrītu noteikumiem un nosacījumiem *</span>
                  <br />
                  <span className="text-sm text-gray-600">
                    Es apstiprinu, ka sniegtā informācija ir patiesa un piekrītu Super Week nometnes{' '}
                    <a
                      href="/noteikumi"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 underline underline-offset-2 font-medium"
                    >
                      noteikumiem un nosacījumiem
                    </a>
                    . Es saprotu, ka dalība aktivitātēs ir uz savu atbildību.
                  </span>
                </label>
              </div>
              {errors.agreement && <p className="text-red-500 text-sm">{errors.agreement}</p>}

              {/* Validation summary next to the button — field errors higher up
                  the form are otherwise easy to miss when submitting. */}
              {Object.values(errors).some(Boolean) && (
                <p className="text-red-600 text-sm font-medium text-center" role="alert">
                  Lūdzu pārbaudi formu — daži obligātie lauki nav aizpildīti vai satur kļūdas.
                </p>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting || selectedGroupFull}
                whileHover={{ scale: isSubmitting || selectedGroupFull ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting || selectedGroupFull ? 1 : 0.98 }}
                className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 text-white ${
                  isSubmitting || selectedGroupFull
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 shadow-lg hover:shadow-xl'
                }`}
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center justify-center">
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Reģistrē...
                  </span>
                ) : selectedGroupFull ? (
                  'Šī nometne ir pilna — izvēlies otru grupu'
                ) : (
                  'Reģistrēties nometnei'
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Flipping SEASON.registrationOpen swaps the whole section between the
// coming-soon card and the live form — no other code change needed.
const Registration: React.FC = () => {
  return SEASON.registrationOpen ? <RegistrationForm /> : <RegistrationClosed />;
};

export default Registration;
